import { useEffect, useState, useCallback, useRef } from 'preact/hooks'
import { lookupsApi } from '@/api'
import type { VariableCatalogResponse } from '@/types/lookups'

/**
 * How one fetch settled. The shared promise resolves to this rather than to bare data so a
 * consumer that JOINS a request in flight reaches the same state as the one that started it,
 * error message included — resolving to `data | null` left a joiner unable to tell a failed
 * request from an empty catalog, so it rendered empty-state text over a real failure.
 */
type CatalogResult =
  | { data: VariableCatalogResponse; error: null }
  | { data: null; error: string }

const cache = new Map<string, VariableCatalogResponse>()
const inFlight = new Map<string, Promise<CatalogResult>>()

export interface UseVariableCatalogResult {
  data: VariableCatalogResponse | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

function cacheKey(workspaceId: string, webId: string): string {
  return `${workspaceId}::${webId}`
}

/**
 * Loads the web's template-variable catalog (ORB-466) — every token available to this web,
 * grouped, with each one's live resolved value.
 *
 * Two consumers can mount simultaneously inside a single builder (the reference panel and the
 * Unlayer merge-tag seeding), so this mirrors useEmailRuleLookup's cache + in-flight dedup
 * rather than useEmailTypesLookup's simpler cache-only shape — otherwise opening the panel
 * fires a second identical request. It diverges from useEmailRuleLookup in what the shared
 * promise resolves to (see CatalogResult), because with two guaranteed consumers the one that
 * joins a request has to end up in the same loading/error state as the one that started it.
 *
 * The cache is stale-while-revalidate, NOT read-through. Every consumer mount revalidates; the
 * cached copy only exists to avoid a skeleton flash while that happens. This is deliberate and
 * load-bearing — the values are live reads of a Web the user edits elsewhere, so a cache that
 * answers without asking is a cache that lies (ORB-475). Chose this over invalidating from each
 * Web-mutation call site: that needs every current and future save handler to remember, and the
 * one that forgets fails silently and looks exactly like missing data.
 */
export function useVariableCatalog(
  workspaceId: string | undefined,
  webId: string | undefined
): UseVariableCatalogResult {
  const key = workspaceId && webId ? cacheKey(workspaceId, webId) : null
  const [data, setData] = useState<VariableCatalogResponse | null>(key ? cache.get(key) ?? null : null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The key this hook currently wants, readable from inside a settled fetch. A request that
  // lands after the ids moved on belongs to a web the caller has left, and writing its result
  // would undo the reset below.
  const activeKeyRef = useRef<string | null>(key)

  const fetchCatalog = useCallback(async (force = false) => {
    if (!workspaceId || !webId) {
      activeKeyRef.current = null
      // The panel passes undefined ids whenever it's closed while staying mounted, so this runs
      // on every close. Clearing data but leaving isLoading/error behind meant a reopen could
      // flash the previous open's error or skeleton before the new request replaced it.
      setData(null)
      setIsLoading(false)
      setError(null)
      return
    }
    const k = cacheKey(workspaceId, webId)
    const keyChanged = activeKeyRef.current !== k
    activeKeyRef.current = k

    const apply = (result: CatalogResult) => {
      if (activeKeyRef.current !== k) return
      // A failed revalidation must not blank a catalog we already have. Fall back to the last
      // good response so the panel keeps showing real values; the panel only renders the error
      // block when it has no data at all, so the failure stays quiet behind live content.
      setData(result.data ?? cache.get(k) ?? null)
      setError(result.error)
      setIsLoading(false)
    }

    // Stale-while-revalidate. A cache hit is a seed to paint immediately, never the final answer:
    // every value in this catalog is a point-in-time read of the Web, and the user adds speakers,
    // agenda items and schedule in a DIFFERENT screen while this module-level cache survives the
    // panel closing, the builder unmounting, and everything short of a page reload. Returning the
    // cached copy and stopping is how the panel spent nine minutes insisting an event had no
    // speakers, four minutes after one was saved — while the API would have returned it on any
    // fresh call (ORB-475).
    const cached = cache.get(k)
    if (cached) setData(cached)
    // Moved to a different Web with nothing cached for it: drop the previous one's values rather
    // than leaving them on screen under the new Web's name. Gated on the key CHANGING, not merely
    // on a cache miss — refetch() deletes the key before re-fetching, and blanking there would
    // turn a manual refresh of a healthy panel into an empty one.
    else if (keyChanged) setData(null)

    // Only show the skeleton when there is genuinely nothing to look at — a revalidation behind a
    // warm cache is invisible by design, and the values swap in when they land. Still set before
    // the in-flight branch below, not after it: a consumer that JOINS a running request needs the
    // same skeleton as the one that started it, and the panel and the Unlayer merge-tag seeding
    // mount together inside one builder, so joining is the ordinary path rather than the edge case.
    setIsLoading(!cached)
    setError(null)

    const existing = inFlight.get(k)
    if (existing && !force) {
      apply(await existing)
      return
    }

    const promise = lookupsApi.getVariableCatalog(workspaceId, webId).then((response): CatalogResult => {
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to load the variable catalog' }
      }
      cache.set(k, response.data)
      return { data: response.data, error: null }
    }).finally(() => {
      inFlight.delete(k)
    })

    inFlight.set(k, promise)
    apply(await promise)
  }, [workspaceId, webId])

  useEffect(() => {
    void fetchCatalog()
  }, [fetchCatalog])

  // Every mount already revalidates, so this is for the cases that aren't a mount: the error-state
  // retry, and any caller that wants to force a read without waiting for the panel to reopen. It
  // drops the cached seed and bypasses an in-flight join so the caller genuinely gets a fresh read.
  const refetch = useCallback(() => {
    if (key) cache.delete(key)
    void fetchCatalog(true)
  }, [key, fetchCatalog])

  return { data, isLoading, error, refetch }
}
